import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import { Colors } from '@silvergatedelivery/constants';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { deliveryStaffPodByElderId } from 'graphql/mutations';
import { request } from 'utilities/graph';
import { toastr } from 'react-redux-toastr';
import DeliveryDiaryForm from 'forms/DeliveryDiaryForm/DeliveryDiaryForm';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import cache from 'utilities/cache';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
    overflow: 'auto',
    backgroundColor: Colors.background.light,
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    minHeight: '100vh',
  },
  paper: {
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  title: {
    marginBottom: theme.spacing(3),
    color: Colors.primaryText,
    fontWeight: 600,
  },
  phoneField: {
    'marginBottom': theme.spacing(3),
    '& .MuiOutlinedInput-root': {
      backgroundColor: Colors.background.white,
    },
  },
  button: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  submitButton: {
    minWidth: 120,
    height: 48,
    borderRadius: 24,
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
  },
  successIcon: {
    fontSize: 80,
    color: Colors.primary,
    marginBottom: theme.spacing(2),
  },
  successText: {
    color: Colors.primaryText,
    fontWeight: 600,
  },
  errorText: {
    color: Colors.error,
    fontWeight: 600,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.spacing(2),
  },
}));

export default function PodByQRCode({ id }) {
  const classes = useStyles();
  const [phoneNumber, setPhoneNumber] = useState(cache.get('app:pod:phoneNumber') || '');
  const [deliveryDiary, setDeliveryDiary] = useState({});
  const [completed, setCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadImageFailed, setUploadImageFailed] = useState(false);

  const handleSubmit = async () => {
    if (!phoneNumber || !phoneNumber.replace(/\D/g, '')) {
      toastr.error('請輸入電話號碼');
      return;
    }
    if (!id) {
      toastr.error('無法取得長輩ID');
      return;
    }
    if (!deliveryDiary.deliveryResult) {
      toastr.error('請選擇送餐狀態');
      return;
    }
    const input = {
      elderId: id,
      deliveryStaffPhoneNumber: phoneNumber,
      diary: deliveryDiary.diary,
      deliveryResult: deliveryDiary.deliveryResult,
      checkListReminder: deliveryDiary.checkListReminder || false,
      checkListChat: deliveryDiary.checkListChat || false,
      imageFileCount: deliveryDiary.imageFiles?.length || 0,
      source: 'QR Code',
    };
    try {
      setIsLoading(true);
      const { data: { deliveryStaffPodByElderId: result } } = await request(deliveryStaffPodByElderId, { input });
      console.log(result);
      if (result.message === 'success') {
        const { uploadImageUrls } = result;
        if (uploadImageUrls && uploadImageUrls.length > 0 && deliveryDiary.imageFiles) {
          await Promise.all(
            uploadImageUrls.map(async (url, index) => {
              let retryCount = 0;
              const maxRetries = 3;
              while (retryCount < maxRetries) {
                try {
                  const file = deliveryDiary.imageFiles[index % deliveryDiary.imageFiles.length];
                  const response = await fetch(file.preview);
                  let blob = await response.blob();
                  if (file.type !== 'image/jpeg') {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const img = new Image();
                    await new Promise((resolve) => {
                      img.onload = resolve;
                      img.src = file.preview;
                    });
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);
                    blob = await new Promise((resolve) => {
                      canvas.toBlob(resolve, 'image/jpeg', 0.9);
                    });
                  }
                  await fetch(url, {
                    method: 'PUT',
                    body: blob,
                    headers: {
                      'Content-Type': 'image/jpeg',
                    },
                  });
                  break;
                } catch (error) {
                  console.error(error);
                  retryCount++;
                  if (retryCount === maxRetries) {
                    setUploadImageFailed(true);
                  } else {
                    await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount));
                  }
                }
              }
            }),
          );
        }
        cache.set('app:pod:phoneNumber', phoneNumber);
        setCompleted(true);
      } else if (result.errors && result.errors[0]) {
        toastr.error(result.errors[0]);
      }
    } catch (e) {
      console.log(e);
      if (e.errors && e.errors[0] && e.errors[0].message) {
        toastr.error(e.errors[0].message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (updatedFormData) => {
    console.log(updatedFormData);
    setDeliveryDiary(updatedFormData);
  };

  if (completed) {
    return (
      <div className={classes.root}>
        <Container maxWidth="md" className={classes.container}>
          <Box className={classes.successContainer}>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography variant="h4" className={classes.successText}>
              訂單已完成
            </Typography>
            {uploadImageFailed && (
              <Typography variant="h6" className={classes.errorText}>
                訂單已完成送達回報，但是照片上傳失敗，請聯繫社工或相關管理人員
              </Typography>
            )}
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h4" className={classes.title}>
          請輸入您的電話號碼 *
        </Typography>
        <TextField
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          fullWidth
          variant="outlined"
          label="電話號碼"
          placeholder="請輸入您的電話號碼"
          className={classes.phoneField}
        />
        <Paper className={classes.paper}>
          <DeliveryDiaryForm onChange={handleFormChange} />
        </Paper>
        <div className={classes.button}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            className={classes.submitButton}
          >
            {isLoading ? (
              <>
                上傳中
                <div className={classes.loadingContainer}>
                  <CircularProgress size={20} color="inherit" />
                </div>
              </>
            ) : (
              '確定'
            )}
          </Button>
        </div>
      </Container>
    </div>
  );
}

PodByQRCode.propTypes = {
  id: PropTypes.string.isRequired,
};
